import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { getSession, onAuthStateChange } from '../../utils/auth'
import { isSupabaseConfigured } from '../../utils/supabase'

export default function ProtectedRoute({ children, requireAdmin = false }) {
    const [loading, setLoading] = useState(true)
    const [authenticated, setAuthenticated] = useState(false)
    const [user, setUser] = useState(null)
    const location = useLocation()

    useEffect(() => {
        // If Supabase is not configured, don't allow access to protected routes
        if (!isSupabaseConfigured()) {
            console.error('Supabase not configured - blocking protected route access')
            setAuthenticated(false)
            setLoading(false)
            return
        }

        // Check initial session
        const checkSession = async () => {
            try {
                const session = await getSession()
                if (session && session.user) {
                    setAuthenticated(true)
                    setUser(session.user)
                } else {
                    setAuthenticated(false)
                    setUser(null)
                }
            } catch (error) {
                console.error('Error checking session:', error)
                setAuthenticated(false)
            }
            setLoading(false)
        }

        checkSession()

        // Subscribe to auth changes
        const { data: { subscription } } = onAuthStateChange((event, session) => {
            if (session && session.user) {
                setAuthenticated(true)
                setUser(session.user)
            } else {
                setAuthenticated(false)
                setUser(null)
            }
            setLoading(false)
        })

        return () => subscription?.unsubscribe()
    }, [])

    // Show loading spinner while checking auth
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-primary-400 animate-spin mx-auto mb-4" />
                    <p className="text-white/60">Checking authentication...</p>
                </div>
            </div>
        )
    }

    // If Supabase is not configured, show error message
    if (!isSupabaseConfigured()) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950">
                <div className="text-center max-w-md p-8">
                    <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Setup Required</h1>
                    <p className="text-white/60 mb-6">
                        Authentication is not configured. Please set up Supabase environment variables.
                    </p>
                    <a href="/" className="btn-primary">
                        Back to Home
                    </a>
                </div>
            </div>
        )
    }

    // If not authenticated, redirect to sign in page
    if (!authenticated || !user) {
        // Store the intended destination for redirect after login
        return <Navigate to="/signin" state={{ from: location }} replace />
    }

    return children
}
